import * as React from "react";
import request from "src/utils/request";
import { useParams } from "react-router-dom";
import { useActiveIntegrationsTeams } from "src/scenes/teams/dashboard/active-integrations-provider-teams";
import { useAuth } from "src/components/providers/auth-context";

const TeamSprintsContext = React.createContext<{ allProjects?: { projects: [] } }>({});
TeamSprintsContext.displayName = "SprintsContextTeams";

const TeamSprintsContextProvider = (props) => {
    const { teamId } = useParams();
    const { isDemo, userHasTeamsSubscription } = useAuth();
    const { activeIntegrations: activeIntegrationsTeams } = useActiveIntegrationsTeams();
    const [selectedSprint, setSelectedSprint] = React.useState("");
    const [allSprints, setAllSprints] = React.useState([]);
    const [allProjects, setAllProjects] = React.useState<{ projects: { projectKey: string }[] }>({
        projects: [],
    });
    const isTeamJiraIntegrated =
        activeIntegrationsTeams &&
        activeIntegrationsTeams.data &&
        activeIntegrationsTeams.data.integrations.find((int) => int.connected) &&
        activeIntegrationsTeams.data.integrations.find((int) => int.name === "jira").connected;
    const isTeamNotionIntegrated =
        activeIntegrationsTeams &&
        activeIntegrationsTeams.data &&
        activeIntegrationsTeams.data.integrations.find((int) => int.connected) &&
        activeIntegrationsTeams.data.integrations.find((int) => int.name === "notion").connected;

    const getSprints = async () => {
        let response;
        let projectRes;
        if (userHasTeamsSubscription && (isTeamJiraIntegrated || isTeamNotionIntegrated)) {
            [response, projectRes] = await Promise.all([
                request(`/api/data/fundamentals/teams/${teamId}/v1/jira-sprints`, {
                    method: "GET",
                }),
                request(`/api/data/fundamentals/teams/${teamId}/v1/jira-projects`, {
                    method: "GET",
                }),
            ]);
        }
        if (
            isDemo ||
            (!isTeamJiraIntegrated && !isTeamNotionIntegrated) ||
            !userHasTeamsSubscription
        ) {
            // @ts-expect-error: Ignore TS1323 for dynamic import
            response = (await import("../../dashboard/mock-data/work-fundamentals/jira-sprints"))
                .default;
            // @ts-expect-error: Ignore TS1323 for dynamic import
            projectRes = (await import("../../dashboard/mock-data/work-fundamentals/jira-projects"))
                .default;
        }
        setAllSprints(response.filter((sprint) => sprint.startDate && sprint.endDate));
        setSelectedSprint(response[0]);
        if (projectRes && projectRes.projects && projectRes.projects.length > 0) {
            setAllProjects({ projects: [...projectRes.projects] });
        } else {
            setAllProjects({ projects: [] });
        }
    };

    React.useEffect(() => {
        if (activeIntegrationsTeams.status === "SUCCESS") getSprints();
    }, [activeIntegrationsTeams.status]);

    const handleSprintChange = (newSelectedSprint) => {
        setSelectedSprint(newSelectedSprint);
    };

    const value = React.useMemo(
        () => ({ handleSprintChange, selectedSprint, allSprints, allProjects }),
        [handleSprintChange, selectedSprint, allSprints, allProjects]
    );

    return <TeamSprintsContext.Provider value={value} {...props} />;
};

const useTeamSprints = () => {
    const context = React.useContext(TeamSprintsContext);
    if (context === undefined) {
        throw new Error(`useSprints must be used within a TeamSprintsProvider`);
    }
    return context;
};

export { TeamSprintsContextProvider, useTeamSprints };
